<template>
  <div class="ma-4">
    <v-card class="rounded-lg">
      <v-app-bar flat dense color="grey lighten-4" class="rounded-t-lg px-3">
        <v-btn icon small class="mr-2 grey lighten-3" @click="$router.go(-1)">
          <v-icon>navigate_before</v-icon>
        </v-btn>

        <span class="text-h6 font-weight-medium grey--text text--darken-3">
          Behavior Report
        </span>

        <v-spacer></v-spacer>

        <report-list-actions
          :reportOptions="reportOptions"
          :downloadFilename="reportListFilename"
          class="mr-2"
        />
      </v-app-bar>

      <v-divider></v-divider>

      <v-card-text class="pa-6 grey lighten-5">
        <v-row dense>
          <v-col cols="12" md="3">
            <v-menu
              ref="calendarMenu"
              v-model="calendarMenu"
              :close-on-content-click="false"
              :return-value.sync="date"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="filters.startDate"
                  label="Start Date"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  :rules='requiredField'
                  @change="behaviorQuery()"
                  outlined
                  dense
                  hide-details
                  class="rounded-lg"
                  background-color="white"
                  clearable
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="filters.startDate"
                no-title
                scrollable
                :rules='requiredField'
                @change="behaviorQuery()"
                class="rounded-lg"
                clearable
                :max="filters.endDate"
              >
                <v-spacer></v-spacer>
                <v-btn
                  text
                  color="primary"
                  @click="calendarMenu = false"
                >
                  Cancel
                </v-btn>
                <v-btn
                  text
                  color="primary"
                  @click="$refs.calendarMenu.save(date)"
                >
                  OK
                </v-btn>
              </v-date-picker>
            </v-menu>
          </v-col>

          <v-col cols="12" md="3">
            <v-menu
              ref="calendar2Menu"
              v-model="calendar2Menu"
              :close-on-content-click="false"
              :return-value.sync="date"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="filters.endDate"
                  label="End Date"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  @change="behaviorQuery"
                  outlined
                  dense
                  hide-details
                  class="rounded-lg"
                  background-color="white"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="filters.endDate"
                no-title
                scrollable
                @change="behaviorQuery"
                class="rounded-lg"
                clearable
                :min="filters.startDate"
              >
                <v-spacer></v-spacer>
                <v-btn
                  text
                  color="primary"
                  @click="calendar2Menu = false"
                >
                  Cancel
                </v-btn>
                <v-btn
                  text
                  color="primary"
                  @click="$refs.calendar2Menu.save(date)"
                >
                  OK
                </v-btn>
              </v-date-picker>
            </v-menu>
          </v-col>

          <v-col cols="12" md="2">
            <v-select
              label="Select a Program"
              :items="programs"
              item-text="Name"
              item-value="Name"
              v-model="filters.program"
              clearable
              @change="behaviorQuery"
              outlined
              dense
              hide-details
              class="rounded-lg"
              background-color="white"
            ></v-select>
          </v-col>

          <v-col cols="12" md="2">
            <v-select
              label="Client Status"
              :items="clientStatuses"
              v-model="clientStatus"
              clearable
              @change="behaviorQuery"
              outlined
              dense
              hide-details
              class="rounded-lg"
              background-color="white"
            ></v-select>
          </v-col>

          <v-col cols="12" md="2">
            <v-select
              label="Select a Client"
              :items="filteredClients"
              item-text="Name"
              item-value="._id"
              v-model="filters.ClientID"
              clearable
              @change="behaviorQuery"
              outlined
              dense
              hide-details
              class="rounded-lg"
              background-color="white"
            ></v-select>
          </v-col>
        </v-row>

        <v-row dense class="mt-4">
          <v-col cols="12" md="3">
            <v-select
              label="Select a Behavior"
              :items="behavior"
              item-text="Behavior"
              item-value="Behavior"
              v-model="filters.selectedBehavior"
              clearable
              @change="behaviorQuery"
              outlined
              dense
              hide-details
              class="rounded-lg"
              background-color="white"
            ></v-select>
          </v-col>
          <v-col cols="12" md="3">
            <v-btn
              color="primary"
              block
              height="40"
              class="rounded-lg"
              :loading="isLoading"
              elevation="1"
              @click="behaviorQuery"
            >
              <v-icon left>mdi-magnify</v-icon>
              Search
            </v-btn>
          </v-col>
          <v-spacer></v-spacer>
          <v-col cols="12" md="3">
            <v-btn
              color="primary"
              class="rounded-lg"
              :disabled="isLoading"
              @click="generateBehaviorTrendsChartData()"
              elevation="1"
              height="40"
            >
              <v-icon left>mdi-chart-bar</v-icon>
              Behavior Trends Chart
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Data Table -->
      <v-card-text class="pa-6">
        <v-data-table
          :page="page"
          :pageCount="pageCount"
          :server-items-length="itemCount"
          :options.sync="options"
          :headers="headers"
          :items="behaviors"
          sort-by="DateTime"
          sort-desc
          class="elevation-1 rounded-lg"
          :loading="isLoading"
          loading-text="Loading Behavior Reports"
          :footer-props="{
            'items-per-page-options': [5, 10, 20, 50],
            showFirstLastPage: true,
            'items-per-page-text': 'Rows per page:',
            class: 'rounded-lg',
          }"
        >
          <template v-slot:item.select="{ item }">
            <v-btn
              x-small
              color="primary"
              @click="viewBehaviorReport(item)"
              class="px-3 mr-2"
            >
              <v-icon left x-small>mdi-eye</v-icon>
              View
            </v-btn>
            <v-btn
              x-small
              color="success"
              @click="downloadBehaviorReport(item)"
              class="px-3"
            >
              <v-icon left x-small>mdi-download</v-icon>
              Download
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Behavior Trends Chart Dialog -->
    <v-dialog v-model="isBehaviorTrendsChartDialogVisible" max-width="80%" class="rounded-lg">
      <v-card class="rounded-lg">
        <v-card-title class="primary white--text">
          <span class="text-h6">{{ isComparisonMode ? 'Behavior Comparison: ' + getPeriod1Label() + ' vs ' + getPeriod2Label() : 'Behavior Trends Over Time' }}</span>
        </v-card-title>
        <v-card-text class="pt-6">
          <!-- Time Period Comparison Controls -->
          <v-expansion-panels v-model="comparisonPanel" class="mb-4">
            <v-expansion-panel class="comparison-panel">
              <v-expansion-panel-header>
                <template v-slot:default>
                  <div class="d-flex align-center justify-center w-100">
                    <v-icon left class="mr-2 white--text">mdi-chart-timeline-variant</v-icon>
                    <span class="text-h6 font-weight-medium white--text">View Time Period Comparison</span>
                  </div>
                </template>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row dense>
                  <v-col cols="12" md="3">
                    <v-select
                      label="Comparison Type"
                      :items="comparisonTypes"
                      v-model="selectedComparisonType"
                      outlined
                      dense
                      @change="updateComparisonPeriods()"
                    ></v-select>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-menu
                      ref="period1StartMenu"
                      v-model="period1StartMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="period1.startDate"
                          label="Period 1 Start"
                          prepend-icon="mdi-calendar"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                          outlined
                          dense
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="period1.startDate"
                        @input="period1StartMenu = false"
                        color="primary"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-menu
                      ref="period1EndMenu"
                      v-model="period1EndMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="period1.endDate"
                          label="Period 1 End"
                          prepend-icon="mdi-calendar"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                          outlined
                          dense
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="period1.endDate"
                        @input="period1EndMenu = false"
                        color="primary"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-chip
                      color="primary"
                      text-color="white"
                      class="ma-2"
                    >
                      {{ getPeriod1Label() }}
                    </v-chip>
                  </v-col>
                </v-row>
                <v-row dense>
                  <v-col cols="12" md="3">
                    <v-divider class="my-2"></v-divider>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-menu
                      ref="period2StartMenu"
                      v-model="period2StartMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="period2.startDate"
                          label="Period 2 Start"
                          prepend-icon="mdi-calendar"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                          outlined
                          dense
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="period2.startDate"
                        @input="period2StartMenu = false"
                        color="primary"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-menu
                      ref="period2EndMenu"
                      v-model="period2EndMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="period2.endDate"
                          label="Period 2 End"
                          prepend-icon="mdi-calendar"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                          outlined
                          dense
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="period2.endDate"
                        @input="period2EndMenu = false"
                        color="primary"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-chip
                      color="secondary"
                      text-color="white"
                      class="ma-2"
                    >
                      {{ getPeriod2Label() }}
                    </v-chip>
                  </v-col>
                </v-row>
                <v-row class="mt-4">
                  <v-col cols="12" class="text-center">
                    <v-btn
                      color="success"
                      class="rounded-lg"
                      :disabled="isLoading"
                      @click="generateComparisonChartData()"
                      elevation="1"
                    >
                      <v-icon left>mdi-chart-timeline-variant</v-icon>
                      Generate Comparison
                    </v-btn>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>

          <!-- Summary Section -->
          <div v-if="behaviorTrendsChartData && behaviorTrendsChartData.datasets" class="mb-4">
            <v-row>
              <v-col cols="12" md="3">
                <v-card outlined class="pa-3 text-center">
                  <div class="text-h6 primary--text">{{ getTotalBehaviors() }}</div>
                  <div class="text-caption">Total Behaviors</div>
                </v-card>
              </v-col>
              <v-col cols="12" md="3">
                <v-card outlined class="pa-3 text-center">
                  <div class="text-h6 primary--text">{{ getDateRange() }}</div>
                  <div class="text-caption">Date Range</div>
                </v-card>
              </v-col>
              <v-col cols="12" md="3">
                <v-card outlined class="pa-3 text-center">
                  <div class="text-h6 primary--text">{{ getMostCommonBehavior() }}</div>
                  <div class="text-caption">Most Common Behavior</div>
                </v-card>
              </v-col>
              <v-col cols="12" md="3">
                <v-card outlined class="pa-3 text-center">
                  <div class="text-h6 primary--text">{{ getAverageIntensity() }}</div>
                  <div class="text-caption">Average Intensity</div>
                </v-card>
              </v-col>
              <v-col cols="12" md="3" v-if="isComparisonMode">
                <v-card outlined class="pa-3 text-center">
                  <div class="text-h6" :class="getTrendColor()">{{ getTrendAnalysis() }}</div>
                  <div class="text-caption">Trend Analysis</div>
                </v-card>
              </v-col>
            </v-row>
          </div>
          
          <BarChart
            :loading="isLoading"
            :chart-data="behaviorTrendsChartData"
            chart-id="behaviorTrendsChart"
            :chart-options="behaviorChartOptions"
          />
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            color="grey"
            text
            @click="isBehaviorTrendsChartDialogVisible = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import axios from 'axios';
import {mapGetters} from "vuex";
import Swal from "sweetalert2";
import {STATUS_ARRAY} from '@/enums/UserStatusEnum';
import ReportListActions from '@/components/Report/ReportListActions.vue';
import {DateTime} from "luxon";
import DateTimeUtil from "../../../utils/date-time.util";
import BarChart from '../../components/shared/BarChart';
import moment from 'moment';

const url = require("../../client_config/config").url;

export default {
  components: {
    BarChart,
    ReportListActions
  },
  data: () => ({
    headers: [
      {
        text: 'Date',
        align: 'start',
        sortable: true,
        value: 'DateTime',
      },
      {text: "Client", value: "ClientInfo.Name"},
      {text: "Behaviors", value: "Behaviors"},
      {text: "Program", value: "Program"},
      {text: "Intensity", value: "Intensity"},
      {text: "Staff", value: "CompletedBy"},
      {
        text: "",
        value: "select",
        sortable: false,
      },

      // { text: "Actions", value: "actions", sortable: false },
    ],
    date: (new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().substr(0, 10),
    clients: [],
    programs: ["Day Program", "Host Home", "Respite", "Residential", "Supported Living"],
    program: '',
    behaviors: [],
    startDate: "",
    endDate: "",
    calendarMenu: false,
    calendar2Menu: false,
    report: '',
    clientName: '',
    isLoading: false,
    state: localStorage.getItem('userState'),
    userState: localStorage.getItem('userState'),
    requiredField: [
      v => !!v || 'This is required',
    ],
    behavior: [],
    selectedBehavior: '',
    page: 1,
    pageCount: 1,
    itemCount: 0,
    options: {},
    clientStatus: "",
    clientStatuses: STATUS_ARRAY,
    filteredClients: [],
    filters: {
      startDate: '',
      endDate: '',
      program: '',
      clientStatus: '',
      ClientID: '',
      selectedBehavior: '',
    },
    snackbarColor: "success",
    // Chart-related data
    isBehaviorTrendsChartDialogVisible: false,
    behaviorTrendsChartData: {
      labels: [],
      datasets: []
    },
    comparisonPanel: null,
    comparisonTypes: [
      { text: 'Previous Month vs Current Month', value: 'previousMonthVsCurrentMonth' },
      { text: 'Year Over Year (Same Date Range)', value: 'yearOverYear' },
      { text: 'Last Year vs Previous Month', value: 'lastYearVsPreviousMonth' },
      { text: 'Last Year vs Last 7 days', value: 'lastYearVsLast7Days' },
      { text: 'Previous Month vs Last 7 days', value: 'previousMonthVsLast7Days' },
      { text: 'Last Year vs Current Month', value: 'lastYearVsCurrentMonth' },
      { text: 'Last 7 days vs Current Month', value: 'last7DaysVsCurrentMonth' }
    ],
    selectedComparisonType: 'previousMonthVsCurrentMonth',
    period1: {
      startDate: moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
      endDate: moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD')
    },
    period2: {
      startDate: moment().startOf('month').format('YYYY-MM-DD'),
      endDate: moment().endOf('month').format('YYYY-MM-DD')
    },
    period1StartMenu: false,
    period1EndMenu: false,
    period2StartMenu: false,
    period2EndMenu: false,
    behaviorChartOptions: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        title: {
          display: true,
          text: 'Behavior Trends Over Time (Frequency & Intensity)',
          font: {
            size: 16,
            weight: 'bold'
          }
        },
        legend: {
          display: true,
          position: 'top',
          labels: {
            usePointStyle: true,
            padding: 20
          }
        },
        tooltip: {
          mode: 'index',
          intersect: false,
          callbacks: {
            title: function(context) {
              return 'Date: ' + context[0].label;
            },
            label: function(context) {
              if (context.dataset.label.includes('(Frequency)')) {
                return context.dataset.label + ': ' + context.parsed.y + ' occurrence(s)';
              } else if (context.dataset.label.includes('(Intensity)')) {
                const intensityLevel = context.parsed.y;
                let intensityLabel = 'Unknown';
                if (intensityLevel === 1) intensityLabel = 'Mild';
                else if (intensityLevel === 2) intensityLabel = 'Moderate';
                else if (intensityLevel === 3) intensityLabel = 'Severe';
                else if (intensityLevel === 4) intensityLabel = 'Extreme';
                return context.dataset.label + ': ' + intensityLabel + ' (' + intensityLevel + ')';
              }
              return context.dataset.label + ': ' + context.parsed.y;
            }
          }
        }
      },
      scales: {
        x: {
          title: {
            display: true,
            text: 'Date',
            font: {
              weight: 'bold'
            }
          },
          grid: {
            display: true,
            color: 'rgba(0,0,0,0.1)'
          }
        },
        y: {
          type: 'linear',
          display: true,
          position: 'left',
          title: {
            display: true,
            text: 'Frequency (Number of Behaviors)',
            font: {
              weight: 'bold'
            }
          },
          beginAtZero: true,
          ticks: {
            stepSize: 1,
            precision: 0
          },
          grid: {
            display: true,
            color: 'rgba(0,0,0,0.1)'
          }
        },
        y1: {
          type: 'linear',
          display: true,
          position: 'right',
          title: {
            display: true,
            text: 'Average Intensity Level (1=Mild, 2=Moderate, 3=Severe, 4=Extreme)',
            font: {
              weight: 'bold'
            }
          },
          beginAtZero: true,
          max: 4,
          ticks: {
            stepSize: 1,
            precision: 0,
            callback: function(value) {
              const labels = ['', 'Mild', 'Moderate', 'Severe', 'Extreme'];
              return labels[value] || value;
            }
          },
          grid: {
            drawOnChartArea: false,
          }
        }
      },
      interaction: {
        mode: 'index',
        intersect: false
      }
    }
  }),

  computed: {
    ...mapGetters("timezones", ["getUserTimezone", "getDefaultTz"]),
    isComparisonMode() {
      return this.selectedComparisonType !== 'previousMonthVsCurrentMonth';
    },
    reportOptions() {
        return { 
            query: this.queryParams,
            endpoints: `behaviorReports/printList`,
            dynamic: true
        }
    },
    reportListFilename() {
        return `Behavior Report`;
    },
    queryParams() {
      let startDate;
      let endDate;

      let {page, itemsPerPage, sortBy, sortDesc} = this.options;

      page = page || 1;
      itemsPerPage = itemsPerPage || 10;

      let today = DateTime.now();
      let lastMonth = DateTime.now().minus({months:1});

      let defaultEndDate = today.toFormat('yyyy-MM-dd');
      let defaultStartDate = lastMonth.toFormat('yyyy-MM-dd');
      
      if (this.filters.startDate) {
        startDate = DateTimeUtil.toUTCISOString(DateTimeUtil.toStartOfDay(this.filters.startDate));
      } else {
        startDate = DateTimeUtil.toUTCISOString(defaultStartDate);
      }

      if (this.filters.endDate) {
        endDate = DateTimeUtil.toUTCISOString(DateTimeUtil.toEndOfDay(this.filters.endDate));
      } else {
        endDate = DateTimeUtil.toUTCISOString(defaultEndDate);
      }

      return {
        page: page,
        limit: itemsPerPage,
        program: this.filters.program || '',
        startDate: startDate,
        endDate: endDate,
        clientId: this.filters.ClientID || '',
        state: this.state || '',
        behavior: this.filters.selectedBehavior || '',
        clientStatus: this.filters.clientStatus || '',
        sortField: Array.isArray(sortBy) && sortBy.length > 0 ? sortBy[0] : 'defaultSortField',
        sortDirection: Array.isArray(sortDesc) && sortDesc.length > 0 && sortDesc[0] ? 'desc' : 'asc',
        tz: this.getUserTimezone || this.getDefaultTz
      };
    },
  },

  watch: {
    options: {
      handler() {
        this.behaviorQuery();
      },
      deep: true
    },

    clientStatus: function (val) {
      if (val === '' || val == null) {
        this.filteredClients = this.clients;
      } else {
        this.filteredClients = this.clients.filter((client) => client.Status === val);
      }

      this.filters.clientStatus = val;
    },
    filters: {
      handler() {
        this.setFilters();
      },
      deep: true
    }
  },

  created() {
    this.getFilters();
    this.getClients();
    this.getBehaviors();
    this.behaviorQuery();
  },

  methods: {
    initialize() {
      this.clients = [];
    },

    async getClients() {
      let params = {};
      let endpoint = `${url}/clients`;

      const companyIds = localStorage.getItem('userCompanies');

      params['Company:in'] = JSON.parse(companyIds);
      params['State'] = this.userState;

      await axios.get(endpoint, {params})
        .then((res) => {
          this.clients = res.data.clients;
          this.filteredClients = this.clients;
        });
    },

    downloadBehaviorReport(item) {

      const id = item._id;
      const downloadUrl = `${url}/behaviorReports/download?id=${id}`;

      Swal.fire({
        title: 'Downloading',
        text: 'Please wait while the PDF is being generated and downloaded...',
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      fetch(downloadUrl)
        .then((response) => response.blob())
        .then((blob) => {
          const anchor = document.createElement('a');
          anchor.href = URL.createObjectURL(blob);
          anchor.download = `BehaviorReport-${item._id}.pdf`;

          document.body.appendChild(anchor);
          anchor.click();
          document.body.removeChild(anchor);

          Swal.close();
        })
        .catch((error) => {
          console.error('Error generating or downloading the PDF:', error);
          Swal.fire('Error', 'Failed to generate or download the PDF', 'error');
        });
    },


    behaviorQuery() {
      this.behaviors = [];
      this.isLoading = true;
      const params = this.queryParams;
      axios.get(`${url}/behaviorReports/reports`, {params})
        .then(response => {
          const {docs, totalPages, totalDocs} = response.data;

          this.behaviors = docs.map(behavior => ({
            ...behavior,
            DateTime: DateTimeUtil.toLocalDateTime(behavior.DateTime),
            CompletedBy: this.formatSignature(behavior.CompletedBy)
          }));

          this.pageCount = totalPages;
          this.itemCount = totalDocs;

        }).finally(() => {
        this.isLoading = false;
      })
    },

    getBehaviors() {
      this.behavior = [];
      this.isLoading = true;

      axios.get(`${url}/behaviors`,)
        .then(response => {
          this.behavior = response.data.behaviors;
          this.isLoading = false;
        })

    },



    viewBehaviorReport(item) {
      this.$router.push({
        path: `/behaviorReport/${item.ClientInfo._id}/update/${item._id}`,
        query: {createBehaviorReport: false, clientNav: false}
      });
    },

    formatSignature(completedBy) {
      if (!completedBy || completedBy === "") {
        return "";
      }

      if (completedBy.includes("Z")) {
        const [name, utcTime] = completedBy.split(" - ");
        const localTime = DateTimeUtil.toLocalDateTime(utcTime);
        return `${name} - ${localTime}`;
      }

      return completedBy;
    },

    printTable() {
      window.print();
    },

    getFilters() {
      let filters = localStorage.getItem('behaviorReportFilters');
      if (filters) {
        this.filters = JSON.parse(filters);
        this.clientStatus = this.filters.clientStatus;
      }
    },

    setFilters() {
      localStorage.setItem('behaviorReportFilters', JSON.stringify(this.filters));
    },

    // Chart generation methods
    async generateBehaviorTrendsChartData() {
      this.isBehaviorTrendsChartDialogVisible = true;
      
      const behaviors = this.behaviors || [];
      
      // Group behaviors by date and category, also track intensity
      const dateBehaviorMap = {};
      const dateIntensityMap = {};
      const allDates = new Set();
      const allBehaviors = new Set();

      behaviors.forEach(behavior => {
        let date = null;
        if (behavior.DateTime) {
          const dateTimeStr = behavior.DateTime;
          if (dateTimeStr.includes(' ')) {
            date = dateTimeStr.split(' ')[0];
          } else {
            date = dateTimeStr;
          }
        }
        
        if (date && behavior.Behaviors) {
          const intensity = parseInt(behavior.Intensity) || 0;
          
          // Handle multiple behaviors - behavior.Behaviors can be an array or string
          let behaviorTypes = [];
          
          if (Array.isArray(behavior.Behaviors)) {
            // If it's already an array, use it directly
            behaviorTypes = behavior.Behaviors;
          } else if (typeof behavior.Behaviors === 'string') {
            // If it's a string, check if it contains multiple behaviors
            let behaviorStr = behavior.Behaviors;
            
            // Remove array brackets and quotes if present
            if (behaviorStr.startsWith('[') && behaviorStr.endsWith(']')) {
              behaviorStr = behaviorStr.slice(1, -1);
            }
            if (behaviorStr.startsWith('"') && behaviorStr.endsWith('"')) {
              behaviorStr = behaviorStr.slice(1, -1);
            }
            
            // Split by comma if multiple behaviors are in one string
            if (behaviorStr.includes(',')) {
              behaviorTypes = behaviorStr.split(',').map(b => b.trim());
            } else {
              behaviorTypes = [behaviorStr];
            }
          } else {
            // Convert to string and treat as single behavior
            behaviorTypes = [String(behavior.Behaviors)];
          }
          
          // Process each behavior type separately
          behaviorTypes.forEach(behaviorType => {
            // Clean up individual behavior name
            if (typeof behaviorType !== 'string') {
              behaviorType = String(behaviorType);
            }
            
            // Remove quotes if present
            if (behaviorType.startsWith('"') && behaviorType.endsWith('"')) {
              behaviorType = behaviorType.slice(1, -1);
            }
            
            // Skip empty behaviors
            if (!behaviorType || behaviorType.trim() === '') {
              return;
            }
            
            allDates.add(date);
            allBehaviors.add(behaviorType);
            
            // Track frequency
            if (!dateBehaviorMap[date]) {
              dateBehaviorMap[date] = {};
            }
            if (!dateBehaviorMap[date][behaviorType]) {
              dateBehaviorMap[date][behaviorType] = 0;
            }
            dateBehaviorMap[date][behaviorType]++;
            
            // Track average intensity
            if (!dateIntensityMap[date]) {
              dateIntensityMap[date] = {};
            }
            if (!dateIntensityMap[date][behaviorType]) {
              dateIntensityMap[date][behaviorType] = { total: 0, count: 0 };
            }
            dateIntensityMap[date][behaviorType].total += intensity;
            dateIntensityMap[date][behaviorType].count += 1;
          });
        }
      });

      // Sort dates chronologically
      const sortedDates = Array.from(allDates).sort();
      const sortedBehaviors = Array.from(allBehaviors).sort();

      // Create datasets for each behavior type (frequency)
      const frequencyDatasets = [];
      const intensityDatasets = [];
      const chartColors = [
        'rgb(255, 99, 132)',   // Red
        'rgb(54, 162, 235)',   // Blue
        'rgb(255, 205, 86)',   // Yellow
        'rgb(75, 192, 192)',   // Teal
        'rgb(153, 102, 255)',  // Purple
        'rgb(255, 159, 64)',   // Orange
        'rgb(201, 203, 207)'   // Gray
      ];

      sortedBehaviors.forEach((behaviorType, index) => {
        const color = chartColors[index % chartColors.length];
        
        // Frequency data
        const frequencyData = sortedDates.map(date => {
          return dateBehaviorMap[date] && dateBehaviorMap[date][behaviorType] ? dateBehaviorMap[date][behaviorType] : 0;
        });

        const frequencyDataset = {
          label: `${behaviorType} (Frequency)`,
          data: frequencyData,
          borderColor: color,
          backgroundColor: color,
          borderWidth: 2,
          borderRadius: 4,
          borderSkipped: false,
          yAxisID: 'y'
        };

        // Intensity data (average)
        const intensityData = sortedDates.map(date => {
          if (dateIntensityMap[date] && dateIntensityMap[date][behaviorType]) {
            const { total, count } = dateIntensityMap[date][behaviorType];
            return count > 0 ? (total / count) : 0;
          }
          return 0;
        });

        const intensityDataset = {
          label: `${behaviorType} (Intensity)`,
          data: intensityData,
          borderColor: color,
          backgroundColor: 'transparent',
          borderWidth: 2,
          borderDash: [5, 5],
          pointStyle: 'circle',
          pointRadius: 4,
          pointHoverRadius: 6,
          yAxisID: 'y1'
        };

        frequencyDatasets.push(frequencyDataset);
        intensityDatasets.push(intensityDataset);
      });

      // Format dates for better display
      const formattedLabels = sortedDates.map(date => {
        const dateObj = new Date(date);
        return dateObj.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric',
          year: '2-digit'
        });
      });

      this.behaviorTrendsChartData = {
        labels: formattedLabels,
        datasets: [...frequencyDatasets, ...intensityDatasets]
      };
    },



    async generateComparisonChartData() {
      this.isBehaviorTrendsChartDialogVisible = true;
      
      // Fetch data for both periods
      const period1Data = await this.fetchBehaviorsForPeriod(this.period1.startDate, this.period1.endDate);
      const period2Data = await this.fetchBehaviorsForPeriod(this.period2.startDate, this.period2.endDate);
      
      // Process data for comparison chart
      const comparisonData = this.processComparisonData(period1Data, period2Data);
      
      this.behaviorTrendsChartData = comparisonData;
    },

    async fetchBehaviorsForPeriod(startDate, endDate) {
      const params = {
        page: 1,
        limit: 1000,
        program: this.filters.program || '',
        startDate: DateTimeUtil.toUTCISOString(startDate),
        endDate: DateTimeUtil.toUTCISOString(endDate),
        clientId: this.filters.ClientID || '',
        state: this.state || '',
        behavior: this.filters.selectedBehavior || '',
        clientStatus: this.filters.clientStatus || '',
        sortField: 'DateTime',
        sortDirection: 'asc'
      };

      try {
        const response = await axios.get(`${url}/behaviorReports/reports`, { params });
        return response.data.docs || [];
      } catch (error) {
        console.error('Error fetching behaviors for period:', error);
        return [];
      }
    },

    processComparisonData(period1Data, period2Data) {
      // Get all unique behavior types from both periods
      const allBehaviors = new Set();
      [...period1Data, ...period2Data].forEach(behavior => {
        if (behavior.Behaviors) {
          // Handle multiple behaviors
          let behaviorTypes = [];
          
          if (Array.isArray(behavior.Behaviors)) {
            behaviorTypes = behavior.Behaviors;
          } else if (typeof behavior.Behaviors === 'string') {
            let behaviorStr = behavior.Behaviors;
            
            // Remove array brackets and quotes if present
            if (behaviorStr.startsWith('[') && behaviorStr.endsWith(']')) {
              behaviorStr = behaviorStr.slice(1, -1);
            }
            if (behaviorStr.startsWith('"') && behaviorStr.endsWith('"')) {
              behaviorStr = behaviorStr.slice(1, -1);
            }
            
            // Split by comma if multiple behaviors are in one string
            if (behaviorStr.includes(',')) {
              behaviorTypes = behaviorStr.split(',').map(b => b.trim());
            } else {
              behaviorTypes = [behaviorStr];
            }
          } else {
            behaviorTypes = [String(behavior.Behaviors)];
          }
          
          // Add each behavior type separately
          behaviorTypes.forEach(behaviorType => {
            if (typeof behaviorType !== 'string') {
              behaviorType = String(behaviorType);
            }
            
            // Remove quotes if present
            if (behaviorType.startsWith('"') && behaviorType.endsWith('"')) {
              behaviorType = behaviorType.slice(1, -1);
            }
            
            // Skip empty behaviors
            if (!behaviorType || behaviorType.trim() === '') {
              return;
            }
            
            allBehaviors.add(behaviorType);
          });
        }
      });

      const sortedBehaviors = Array.from(allBehaviors).sort();
      const chartColors = [
        'rgb(255, 99, 132)',   // Red
        'rgb(54, 162, 235)',   // Blue
        'rgb(255, 205, 86)',   // Yellow
        'rgb(75, 192, 192)',   // Teal
        'rgb(153, 102, 255)',  // Purple
        'rgb(255, 159, 64)',   // Orange
        'rgb(201, 203, 207)'   // Gray
      ];

      const frequencyDatasets = [];
      const intensityDatasets = [];
      
      sortedBehaviors.forEach((behaviorType, index) => {
        const color = chartColors[index % chartColors.length];
        
        // Count behaviors for this type in each period
        const period1Behaviors = period1Data.filter(behavior => {
          if (!behavior.Behaviors) return false;
          
          let behaviorTypes = [];
          
          if (Array.isArray(behavior.Behaviors)) {
            behaviorTypes = behavior.Behaviors;
          } else if (typeof behavior.Behaviors === 'string') {
            let behaviorStr = behavior.Behaviors;
            
            // Remove array brackets and quotes if present
            if (behaviorStr.startsWith('[') && behaviorStr.endsWith(']')) {
              behaviorStr = behaviorStr.slice(1, -1);
            }
            if (behaviorStr.startsWith('"') && behaviorStr.endsWith('"')) {
              behaviorStr = behaviorStr.slice(1, -1);
            }
            
            // Split by comma if multiple behaviors are in one string
            if (behaviorStr.includes(',')) {
              behaviorTypes = behaviorStr.split(',').map(b => b.trim());
            } else {
              behaviorTypes = [behaviorStr];
            }
          } else {
            behaviorTypes = [String(behavior.Behaviors)];
          }
          
          // Check if any of the behaviors match the current behaviorType
          return behaviorTypes.some(bt => {
            if (typeof bt !== 'string') {
              bt = String(bt);
            }
            
            // Remove quotes if present
            if (bt.startsWith('"') && bt.endsWith('"')) {
              bt = bt.slice(1, -1);
            }
            
            return bt === behaviorType;
          });
        });
        
        const period2Behaviors = period2Data.filter(behavior => {
          if (!behavior.Behaviors) return false;
          
          let behaviorTypes = [];
          
          if (Array.isArray(behavior.Behaviors)) {
            behaviorTypes = behavior.Behaviors;
          } else if (typeof behavior.Behaviors === 'string') {
            let behaviorStr = behavior.Behaviors;
            
            // Remove array brackets and quotes if present
            if (behaviorStr.startsWith('[') && behaviorStr.endsWith(']')) {
              behaviorStr = behaviorStr.slice(1, -1);
            }
            if (behaviorStr.startsWith('"') && behaviorStr.endsWith('"')) {
              behaviorStr = behaviorStr.slice(1, -1);
            }
            
            // Split by comma if multiple behaviors are in one string
            if (behaviorStr.includes(',')) {
              behaviorTypes = behaviorStr.split(',').map(b => b.trim());
            } else {
              behaviorTypes = [behaviorStr];
            }
          } else {
            behaviorTypes = [String(behavior.Behaviors)];
          }
          
          // Check if any of the behaviors match the current behaviorType
          return behaviorTypes.some(bt => {
            if (typeof bt !== 'string') {
              bt = String(bt);
            }
            
            // Remove quotes if present
            if (bt.startsWith('"') && bt.endsWith('"')) {
              bt = bt.slice(1, -1);
            }
            
            return bt === behaviorType;
          });
        });
        
        const period1Count = period1Behaviors.length;
        const period2Count = period2Behaviors.length;
        
        // Calculate average intensity for each period
        const period1Intensity = period1Behaviors.length > 0 
          ? period1Behaviors.reduce((sum, b) => sum + (parseInt(b.Intensity) || 0), 0) / period1Behaviors.length 
          : 0;
        const period2Intensity = period2Behaviors.length > 0 
          ? period2Behaviors.reduce((sum, b) => sum + (parseInt(b.Intensity) || 0), 0) / period2Behaviors.length 
          : 0;
        
        // Frequency dataset
        const frequencyDataset = {
          label: `${behaviorType} (Frequency)`,
          data: [period1Count, period2Count],
          backgroundColor: [color, color],
          borderColor: [color, color],
          borderWidth: 2,
          borderRadius: 4,
          yAxisID: 'y'
        };
        
        // Intensity dataset
        const intensityDataset = {
          label: `${behaviorType} (Intensity)`,
          data: [period1Intensity, period2Intensity],
          borderColor: color,
          backgroundColor: 'transparent',
          borderWidth: 2,
          borderDash: [5, 5],
          pointStyle: 'circle',
          pointRadius: 4,
          pointHoverRadius: 6,
          yAxisID: 'y1'
        };
        
        frequencyDatasets.push(frequencyDataset);
        intensityDatasets.push(intensityDataset);
      });

      return {
        labels: [this.getPeriod1Label(), this.getPeriod2Label()],
        datasets: [...frequencyDatasets, ...intensityDatasets]
      };
    },

    updateComparisonPeriods() {
      const selectedType = this.selectedComparisonType;
      const now = moment();

      if (selectedType === 'yearOverYear') {
        // For year-over-year, use the current date range but go back one year
        const currentStart = moment(this.filters.startDate || now.startOf('month'));
        const currentEnd = moment(this.filters.endDate || now.endOf('month'));
        
        this.period1.startDate = currentStart.subtract(1, 'year').format('YYYY-MM-DD');
        this.period1.endDate = currentEnd.subtract(1, 'year').format('YYYY-MM-DD');
        this.period2.startDate = currentStart.add(1, 'year').format('YYYY-MM-DD');
        this.period2.endDate = currentEnd.add(1, 'year').format('YYYY-MM-DD');
      } else if (selectedType === 'lastYearVsPreviousMonth') {
        this.period1.startDate = moment().subtract(1, 'year').startOf('month').format('YYYY-MM-DD');
        this.period1.endDate = moment().subtract(1, 'year').endOf('month').format('YYYY-MM-DD');
        this.period2.startDate = moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
        this.period2.endDate = moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD');
      } else if (selectedType === 'lastYearVsLast7Days') {
        this.period1.startDate = moment().subtract(1, 'year').subtract(7, 'days').startOf('day').format('YYYY-MM-DD');
        this.period1.endDate = moment().subtract(1, 'year').subtract(1, 'day').endOf('day').format('YYYY-MM-DD');
        this.period2.startDate = moment().subtract(7, 'days').startOf('day').format('YYYY-MM-DD');
        this.period2.endDate = moment().subtract(1, 'day').endOf('day').format('YYYY-MM-DD');
      } else if (selectedType === 'previousMonthVsLast7Days') {
        this.period1.startDate = moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
        this.period1.endDate = moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD');
        this.period2.startDate = moment().subtract(7, 'days').startOf('day').format('YYYY-MM-DD');
        this.period2.endDate = moment().subtract(1, 'day').endOf('day').format('YYYY-MM-DD');
      } else if (selectedType === 'lastYearVsCurrentMonth') {
        this.period1.startDate = moment().subtract(1, 'year').startOf('month').format('YYYY-MM-DD');
        this.period1.endDate = moment().subtract(1, 'year').endOf('month').format('YYYY-MM-DD');
        this.period2.startDate = now.startOf('month').format('YYYY-MM-DD');
        this.period2.endDate = now.endOf('month').format('YYYY-MM-DD');
      } else if (selectedType === 'previousMonthVsCurrentMonth') {
        this.period1.startDate = moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
        this.period1.endDate = moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD');
        this.period2.startDate = now.startOf('month').format('YYYY-MM-DD');
        this.period2.endDate = now.endOf('month').format('YYYY-MM-DD');
      } else if (selectedType === 'last7DaysVsCurrentMonth') {
        this.period1.startDate = moment().subtract(7, 'days').startOf('day').format('YYYY-MM-DD');
        this.period1.endDate = moment().subtract(1, 'day').endOf('day').format('YYYY-MM-DD');
        this.period2.startDate = now.startOf('month').format('YYYY-MM-DD');
        this.period2.endDate = now.endOf('month').format('YYYY-MM-DD');
      }
    },

    getPeriod1Label() {
      const start = moment(this.period1.startDate);
      const end = moment(this.period1.endDate);
      if (start.isValid() && end.isValid()) {
        return `${start.format('MMM DD, YYYY')} - ${end.format('MMM DD, YYYY')}`;
      }
      return 'N/A';
    },

    getPeriod2Label() {
      const start = moment(this.period2.startDate);
      const end = moment(this.period2.endDate);
      if (start.isValid() && end.isValid()) {
        return `${start.format('MMM DD, YYYY')} - ${end.format('MMM DD, YYYY')}`;
      }
      return 'N/A';
    },

    getTotalBehaviors() {
      if (!this.behaviorTrendsChartData || !this.behaviorTrendsChartData.datasets) {
        return 0;
      }
      
      let total = 0;
      this.behaviorTrendsChartData.datasets.forEach(dataset => {
        total += dataset.data.reduce((sum, count) => sum + count, 0);
      });
      
      return total;
    },

    getDateRange() {
      if (!this.behaviorTrendsChartData || !this.behaviorTrendsChartData.labels || this.behaviorTrendsChartData.labels.length === 0) {
        return 'N/A';
      }
      
      const labels = this.behaviorTrendsChartData.labels;
      const startDate = labels[0];
      const endDate = labels[labels.length - 1];
      
      return `${startDate} - ${endDate}`;
    },

    getMostCommonBehavior() {
      if (!this.behaviorTrendsChartData || !this.behaviorTrendsChartData.datasets || this.behaviorTrendsChartData.datasets.length === 0) {
        return 'N/A';
      }
      
      let maxTotal = 0;
      let mostCommonBehavior = 'N/A';
      
      this.behaviorTrendsChartData.datasets.forEach(dataset => {
        const total = dataset.data.reduce((sum, count) => sum + count, 0);
        if (total > maxTotal) {
          maxTotal = total;
          // Clean up the behavior name - remove array brackets and quotes if present
          let behaviorName = dataset.label;
          if (behaviorName.startsWith('[') && behaviorName.endsWith(']')) {
            behaviorName = behaviorName.slice(1, -1); // Remove brackets
          }
          if (behaviorName.startsWith('"') && behaviorName.endsWith('"')) {
            behaviorName = behaviorName.slice(1, -1); // Remove quotes
          }
          mostCommonBehavior = behaviorName;
        }
      });
      
      return mostCommonBehavior;
    },

    getAverageIntensity() {
      if (!this.behaviors || this.behaviors.length === 0) {
        return 'N/A';
      }
      
      const totalIntensity = this.behaviors.reduce((sum, behavior) => {
        return sum + (parseInt(behavior.Intensity) || 0);
      }, 0);
      
      const average = totalIntensity / this.behaviors.length;
      
      // Convert to intensity label
      let intensityLabel = 'Unknown';
      if (average >= 1 && average < 1.5) intensityLabel = 'Mild';
      else if (average >= 1.5 && average < 2.5) intensityLabel = 'Moderate';
      else if (average >= 2.5 && average < 3.5) intensityLabel = 'Severe';
      else if (average >= 3.5) intensityLabel = 'Extreme';
      
      return `${intensityLabel} (${average.toFixed(1)})`;
    },

    getTrendColor() {
      if (!this.behaviorTrendsChartData || !this.behaviorTrendsChartData.datasets || this.behaviorTrendsChartData.datasets.length === 0) {
        return '';
      }

      const period1Total = this.behaviorTrendsChartData.datasets[0].data.reduce((sum, count) => sum + count, 0);
      const period2Total = this.behaviorTrendsChartData.datasets[0].data.reduce((sum, count) => sum + count, 0);

      if (period1Total === 0 && period2Total === 0) {
        return 'grey--text';
      }

      if (period1Total > period2Total) {
        return 'success--text';
      } else if (period1Total < period2Total) {
        return 'error--text';
      } else {
        return 'primary--text';
      }
    },

    getTrendAnalysis() {
      if (!this.behaviorTrendsChartData || !this.behaviorTrendsChartData.datasets || this.behaviorTrendsChartData.datasets.length === 0) {
        return 'N/A';
      }

      const period1Total = this.behaviorTrendsChartData.datasets[0].data.reduce((sum, count) => sum + count, 0);
      const period2Total = this.behaviorTrendsChartData.datasets[0].data.reduce((sum, count) => sum + count, 0);

      if (period1Total === 0 && period2Total === 0) {
        return 'No behaviors recorded in either period.';
      }

      if (period1Total > period2Total) {
        return 'Behaviors are improving over time.';
      } else if (period1Total < period2Total) {
        return 'Behaviors are getting worse over time.';
      } else {
        return 'Behaviors are staying the same over time.';
      }
    }
  }
}
</script>
<style scoped>
.v-data-table {
  border: 1px solid #e0e0e0;
}

.v-data-table ::v-deep .v-data-table__wrapper {
  overflow-x: auto;
}

.v-data-table ::v-deep tbody tr:hover {
  background-color: #f5f5f5 !important;
}

.v-btn {
  text-transform: none;
  letter-spacing: 0;
}

.v-text-field ::v-deep .v-input__prepend-inner {
  margin-top: 8px !important;
}

.v-select ::v-deep .v-input__prepend-inner {
  margin-top: 8px !important;
}

.rounded-lg {
  border-radius: 8px !important;
}

.rounded-t-lg {
  border-top-left-radius: 8px !important;
  border-top-right-radius: 8px !important;
}

.comparison-panel {
  border: none !important;
  border-radius: 12px !important;
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.15) !important;
  background: #f8f9ff !important;
  overflow: hidden !important;
}

.comparison-panel .v-expansion-panel-header {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%) !important;
  color: white !important;
  border-radius: 10px 10px 0 0 !important;
  min-height: 60px !important;
}

.comparison-panel .v-expansion-panel-header .v-expansion-panel-header__icon {
  color: white !important;
}

.comparison-panel .v-expansion-panel-header .v-expansion-panel-header__icon .v-icon {
  color: white !important;
  filter: brightness(1.5) !important;
}

.comparison-panel .v-expansion-panel-header:hover {
  background: linear-gradient(135deg, #1565c0 0%, #0d47a1 100%) !important;
}

.comparison-panel .v-expansion-panel-content {
  background: white !important;
  border-radius: 0 0 10px 10px !important;
  padding: 20px !important;
}
</style>

<style>
#print {
  display: none
}

@media only screen and (min-width: 768px) {
  .mobile {
    display: none;
  }
}

@media only screen and (max-width: 600px) {
  .desktop {
    display: none;
  }

}


@media only screen and (min-width: 768px) {
  .mobile {
    display: none;
  }
}

@media only screen and (max-width: 600px) {
  .desktop {
    display: none;
  }

}

@media print {
  #print {
    display: block;
    border: none;
    width: 1200px;
    padding-right: 300px;
    margin-left: -200px;

  }


  nav.v-navigation-drawer, header.v-toolbar, button.v-btn, div.v-card {
    display: none;
    border: none;

  }

  @page {
    margin: 0;
  }

  body {
    margin: 1.6cm;
  }


}
</style>
